import React, { useCallback, useRef, useState, useEffect } from "react";
import { useSelector } from "react-redux";
import DeleteIcon from "@mui/icons-material/Delete";
import CircularProgress from "@mui/material/CircularProgress";
import LinearProgress from "@mui/material/LinearProgress";
import Backdrop from "@mui/material/Backdrop";
import Box from "@mui/material/Box";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import ListItem from "@mui/material/ListItem";
import Fab from "@mui/material/Fab";
import AddIcon from "@mui/icons-material/Add";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import ThemedTypography from "./ThemedTypography";
import ThemedIconButton from "./ThemedIconButton";
import MobileEditAddressDialog from "./dialogs/MobileEditAddressDialog";
import { decoratedFetch } from "../req_utils";
import {
  DARK_THEME_INPUT_OUTLINE_COLOR,
  DARK_THEME_TEXT_COLOR,
  DARK_THEME_DISABLED_TEXT_COLOR,
} from "../constants";

const PROGRESS_INCREMENT = 2;
const PROGRESS_TICK = 30;

const MobileAddressesTab = ({ open, accountId, setSnackbarMessage }) => {
  const darkThemeEnabled = useSelector((state) => state.darkThemeEnabled);
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(0);
  const [selectedListItemIdx, setSelectedListItemIdx] = useState(-1);
  const [dialogState, setDialogState] = useState(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const timerRef = useRef(null);
  const addressAlreadyDeleted = useRef(false);
  const [backdropOpen, setBackdropOpen] = useState(false);

  const handleDeleteAddress = (listItemIdx) => {
    decoratedFetch(`/delete_project_address/${addresses[listItemIdx].id}`, {
      method: "DELETE",
    })
      .then((response) => {
        if (response.status !== 200) {
          throw new Error("Unknown error");
        }
        return response.json();
      })
      .then((data) => {
        setAddresses(addresses.filter((address, i) => i !== listItemIdx));
        setSnackbarMessage("Deleted address");
      })
      .catch((error) => {
        console.error(`Error deleting address: ${error}`);
        setSnackbarMessage("An error occurred when deleting address");
      });
  };

  const reset = useCallback(() => {
    setSelectedListItemIdx(-1);
    clearInterval(timerRef.current);
    timerRef.current = null;
    setProgress(0);
    setTimeout(() => (addressAlreadyDeleted.current = false), 100);
  }, []);

  const start = useCallback(
    (listItemIdx) => {
      timerRef.current = setInterval(() => {
        setProgress((prevProgress) => {
          if (prevProgress >= 100) {
            if (!addressAlreadyDeleted.current) {
              addressAlreadyDeleted.current = true;
              handleDeleteAddress(listItemIdx);
              reset();
            }
            return 0;
          }
          return prevProgress + PROGRESS_INCREMENT;
        });
      }, PROGRESS_TICK);
    },
    [reset, handleDeleteAddress],
  );

  const handlePressStart = (listItemIdx) => {
    setSelectedListItemIdx(listItemIdx);
    if (!timerRef.current) start(listItemIdx);
  };

  const handlePressEnd = () => {
    if (!addressAlreadyDeleted.current) reset();
  };

  const listAddresses = () => {
    if (!accountId) {
      setAddresses([]);
      setLoading(false);
      return;
    }

    decoratedFetch(`/list_addresses?account_id=${accountId}`)
      .then((response) => {
        if (response.status !== 200) {
          throw new Error("Unknown error");
        }
        return response.json();
      })
      .then((data) => {
        setAddresses(data);
        setLoading(false);
        setBackdropOpen(false);
      })
      .catch((error) => {
        setSnackbarMessage("Failed to load addresses");
        setLoading(false);
        setBackdropOpen(false);
        console.error(`Error listing addresses: ${error}`);
      });
  };

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    listAddresses();
  }, [open, accountId]);

  const handleAddAddress = () => {
    setCreateDialogOpen(true);
  };

  const handleEditAddress = (address) => {
    setDialogState({ selectedAddress: address });
  };

  const handleDialogClose = () => {
    setDialogState(null);
  };

  const handleCreateDialogClose = () => {
    setCreateDialogOpen(false);
  };

  const handleCreateAddress = (newAddress) => {
    setAddresses((prevAddresses) => [...prevAddresses, newAddress]);
    setCreateDialogOpen(false);
    setTimeout(() => setDialogState({ selectedAddress: newAddress }), 200);
  };

  const handleAddressUpdate = (updatedAddress) => {
    setAddresses((prevAddresses) =>
      prevAddresses.map((address) =>
        address.id === updatedAddress.id
          ? {
              ...address,
              project_address_type: updatedAddress.project_address_type,
              address: updatedAddress.address,
            }
          : address,
      ),
    );
  };

  const formatAddress = (address) => {
    const parts = [
      address.address.line_1,
      address.address.line_2,
      address.address.city,
      address.address.state,
      address.address.zip,
    ].filter((part) => part && part.trim());

    return parts.join(", ");
  };

  return (
    <Box sx={{ height: "100%", position: "relative" }}>
      <Backdrop
        sx={(theme) => ({ color: "#fff", zIndex: theme.zIndex.drawer + 1 })}
        open={backdropOpen}
        onClick={() => setBackdropOpen(false)}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <MobileEditAddressDialog
        parentState={dialogState}
        createDialogOpen={createDialogOpen}
        handleClose={handleDialogClose}
        handleCreateClose={handleCreateDialogClose}
        onAddressUpdate={handleAddressUpdate}
        onCreateAddress={handleCreateAddress}
        accountId={accountId}
        setSnackbarMessage={setSnackbarMessage}
      />
      {addresses?.length ? (
        <List sx={{ py: 0 }}>
          {addresses.map((address, index) => (
            <React.Fragment key={index}>
              <ListItem sx={{ p: 0 }}>
                <Grid container>
                  <Grid
                    item
                    xs={12}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <ListItemButton
                      key={index}
                      onClick={() => handleEditAddress(address)}
                    >
                      <ListItemText
                        primary={address.project_address_type}
                        secondary={
                          <>
                            <p style={{ marginTop: "4px", marginBottom: 0 }}>
                              <strong>{"Address: "}</strong>
                              {formatAddress(address)}
                            </p>
                          </>
                        }
                        primaryTypographyProps={{
                          sx: {
                            fontWeight: "bold",
                            color: darkThemeEnabled
                              ? DARK_THEME_TEXT_COLOR
                              : "inherit",
                          },
                        }}
                        secondaryTypographyProps={{
                          sx: {
                            color: darkThemeEnabled
                              ? DARK_THEME_DISABLED_TEXT_COLOR
                              : "inherit",
                          },
                        }}
                      />
                    </ListItemButton>
                    <ThemedIconButton
                      onMouseDown={() => handlePressStart(index)}
                      onTouchStart={() => handlePressStart(index)}
                      onMouseUp={() => handlePressEnd()}
                      onTouchEnd={() => handlePressEnd()}
                      sx={{
                        width: "54px",
                        height: "54px",
                        "& svg": {
                          fontSize: "24px",
                        },
                        ml: "5px",
                        mr: "10px",
                      }}
                    >
                      <DeleteIcon />
                    </ThemedIconButton>
                  </Grid>
                  {progress > 0 && selectedListItemIdx === index && (
                    <Grid item xs={12} sx={{ mt: "-4px" }}>
                      <LinearProgress variant="determinate" value={progress} />
                    </Grid>
                  )}
                </Grid>
              </ListItem>
              {index < addresses.length - 1 && (
                <Divider
                  component="li"
                  sx={{
                    backgroundColor: darkThemeEnabled
                      ? DARK_THEME_INPUT_OUTLINE_COLOR
                      : "inherited",
                  }}
                />
              )}
            </React.Fragment>
          ))}
        </List>
      ) : loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
          }}
        >
          <CircularProgress size="50px" />
        </Box>
      ) : backdropOpen ? null : !accountId ? (
        <ThemedTypography
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
          }}
        >
          Save account first to add addresses
        </ThemedTypography>
      ) : (
        <ThemedTypography
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
          }}
        >
          No addresses
        </ThemedTypography>
      )}
      {accountId && (
        <Fab
          color="primary"
          aria-label="add"
          size="medium"
          sx={{ position: "fixed", bottom: 20, right: 20 }}
          onClick={handleAddAddress}
        >
          <AddIcon />
        </Fab>
      )}
    </Box>
  );
};

export default MobileAddressesTab;
